import React, { FC, useCallback, useEffect, useState } from "react";
import { getFromLocalStorage } from "core/utils";
import {
  useAppDispatch,
  useAppSelector,
  useDebounce,
  useLocalStorage,
  useUpdateEffect,
} from "hooks";
import i18n from "i18n";
import { IProjectsFilters } from "models/projects";
import { ISortConfig, IUsersPermissionsFilters } from "models/usersPermissions";
import { useTranslation } from "react-i18next";
import { getLookupByName } from "store";
import { getAllUsers } from "store/thunks/usersPermissionsThunk";

import SearchIcon from "@mui/icons-material/Search";
import {
  Autocomplete,
  Box,
  Checkbox,
  Paper,
  SelectChangeEvent,
} from "@mui/material";
import TextField from "@mui/material/TextField";

import styles from "./UsersPermissions.module.scss";

interface Props {
  usersPermissionsPageNumber: number;
  setUsersPermissionsPageNumber: (pageNumber: number) => void;
}

const UsersPermissionsFilters: FC<Props> = (props) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const roles = useAppSelector((state) => state.lookups.roles);

  const getRolesTranslated = useCallback(
    (roles: string[]) => roles.map((role) => t(`roles.${role}`) ?? role),
    [t, i18n.language],
  );

  const countries = useAppSelector((state) => state.lookups.countries);
  const [roleNames, setRoleNames] = useState<string[]>([]);
  const [usersPermissionsFilters, setUsersPermissionsFilter] =
    useLocalStorage<IUsersPermissionsFilters>("usersPermissionsFilters", {
      roleNames: null,
      country: null,
    });

  const [userName, setUserName] = useLocalStorage(
    "usersPermissionsSearchByName",
    "",
  );
  const debouncedUserName = useDebounce(userName);
  const [userEmail, setUserEmail] = useLocalStorage(
    "usersPermissionsByEmail",
    "",
  );
  const debouncedUserEmail = useDebounce(userEmail);

  useEffect(() => {
    dispatch(getLookupByName("roles"));
    dispatch(getLookupByName("countries"));
  }, [t, i18n.language]);

  useUpdateEffect(() => {
    if (props.usersPermissionsPageNumber > 1) {
      props.setUsersPermissionsPageNumber(1);
    } else {
      const pageSize = getFromLocalStorage<number>("usersPermissionsPageSize");
      const sortConfig = getFromLocalStorage<ISortConfig>(
        "usersPermissionsTableSortConfig",
      );
      dispatch(
        getAllUsers({
          sortBy: sortConfig,
          pageSize,
          userName: debouncedUserName,
          email: debouncedUserEmail,
          pageNumber: 1,
          roleNames: roleNames,
        }),
      );
    }
  }, [
    usersPermissionsFilters,
    roleNames,
    debouncedUserName,
    debouncedUserEmail,
  ]);

  const handleUserRoleChange = (event: SelectChangeEvent<string[]>) => {
    setRoleNames(event.target.value as string[]);
  };

  return (
    <div className={styles.usersPermissionsFiltersWrapper}>
      <Box sx={{ flexBasis: "20%", minWidth: 250 }}>
        <div className={styles.searchWrapper}>
          <input
            name="search"
            type="text"
            autoComplete="off"
            value={userEmail}
            onChange={(e) => setUserEmail(e.target.value)}
            placeholder={t("filters.searchByEmail") as string}
            className={styles.search}
          />
          <button type="button" className={styles.searchIconWrapper}>
            <SearchIcon sx={{ color: "#fff" }} />
          </button>
        </div>
      </Box>

      <Box sx={{ flexBasis: "20%", minWidth: 250 }}>
        <div className={styles.searchWrapper}>
          <input
            name="search"
            type="text"
            autoComplete="off"
            value={userName}
            onChange={(e) => setUserName(e.target.value)}
            placeholder={t("filters.searchByName") as string}
            className={styles.search}
          />
          <button type="button" className={styles.searchIconWrapper}>
            <SearchIcon sx={{ color: "#fff" }} />
          </button>
        </div>
      </Box>

      <Box sx={{ flexBasis: "20%", minWidth: 250 }}>
        <Autocomplete
          multiple
          disableCloseOnSelect
          options={roles}
          value={roleNames}
          onChange={(e, newValue) => setRoleNames(newValue)}
          getOptionLabel={(option) => t(`roles.${option}`)}
          renderOption={(props, option, { selected }) => (
            <li {...props} style={{ height: 48 }}>
              <Checkbox
                style={{ marginRight: 8 }}
                checked={selected}
                tabIndex={-1}
                disableRipple
              />
              {t(`roles.${option}`)}
            </li>
          )}
          renderTags={() => null}
          ListboxProps={{
            style: {
              maxHeight: 240,
              overflowY: "auto",
              padding: 0,
              margin: 0,
            },
          }}
          renderInput={(params) => {
            const selectedText = roleNames
              .map((v) => t(`roles.${v}`))
              .join(", ");
            const showPlaceholder = roleNames.length === 0;

            return (
              <TextField
                {...params}
                variant="outlined"
                placeholder={String(t("filters.chooseRoleName"))}
                inputProps={{
                  ...params.inputProps,
                  readOnly: true,
                  value: showPlaceholder ? "" : selectedText,
                  style: {
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    padding: 0,
                    margin: 0,
                    lineHeight: "41px",
                  },
                }}
                InputProps={{
                  ...params.InputProps,
                  sx: {
                    height: "41px",
                    borderRadius: 0,
                    paddingRight: "40px",
                    display: "flex",
                    alignItems: "center",
                    "& fieldset": {
                      borderColor: "#9b9b9b",
                    },
                    "&:hover fieldset": {
                      borderColor: "#9b9b9b",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#80bdff",
                      borderWidth: 2,
                    },
                  },
                }}
              />
            );
          }}
          PaperComponent={(props) => (
            <Paper
              {...props}
              sx={{
                maxHeight: 240,
                minHeight: 240,
                overflow: "visible",
                py: 0,
                px: 0,
                "&::-webkit-scrollbar": {
                  width: "6px",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#C0D4E3",
                  borderRadius: "4px",
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "transparent",
                },
              }}
            />
          )}
        />
      </Box>

      <Box sx={{ flexBasis: "20%", minWidth: 250 }}>
        <Autocomplete
          ListboxProps={{ style: { maxHeight: "250px" } }}
          value={usersPermissionsFilters.country}
          onChange={(event: any, newValue) => {
            if (newValue) {
              setUsersPermissionsFilter((prevState: IProjectsFilters) => ({
                ...prevState,
                country: newValue,
              }));
            } else {
              setUsersPermissionsFilter((prevState: IProjectsFilters) => ({
                ...prevState,
                country: null,
              }));
            }
          }}
          isOptionEqualToValue={(option, value) =>
            value.name === "" ? true : option.id === value.id
          }
          options={countries}
          getOptionLabel={(option) => (option.name ? option.name : "")}
          renderOption={(props: any, option) => {
            return (
              <span {...props} className={styles.dropDownItem}>
                {option.name}
              </span>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              sx={{
                "& .MuiOutlinedInput-notchedOutline": {
                  borderRadius: "0",
                  border: "1px solid #8A8886",
                },
                "& .MuiOutlinedInput-root": {
                  "&:hover fieldset": {
                    border: "1px solid #8A8886",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#80bdff",
                    boxShadow: "0 0 0 0.2rem rgba(0, 123, 255, .25)",
                  },
                },
                padding: "5px 0",
              }}
              size="small"
              variant="outlined"
              placeholder={t("filters.searchByCountryName") as string}
            />
          )}
        />
      </Box>
    </div>
  );
};

export default UsersPermissionsFilters;
